import { Input } from '@agavi/ui';
import { ChevronLeft } from 'lucide-react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import type { SignupFormValues } from '@/types/auth';

import { Button } from '@/components/ui/button';
import {
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { api } from '@/lib/api';

type StepOneProps = {
  onNext: () => void;
  onBack: () => void;
};

export default function StepOne({ onNext, onBack }: StepOneProps) {
  const { t } = useTranslation();
  const form = useFormContext<SignupFormValues>();
  const fullName = form.watch('fullName');
  const email = form.watch('email');
  const canContinue = fullName.trim().length > 0 && email.trim().length > 0;

  const [loading, setLoading] = useState(false);

  async function handleNext() {
    const isValid = await form.trigger(['fullName', 'email']);
    if (!isValid) return;

    setLoading(true);
    try {
      const res = await api.post<{ available: boolean }>('/auth/check-email', { email });
      if (!res.available) {
        form.setError('email', { message: 'This email is already in use' });
        return;
      }
      onNext();
    } catch {
      form.setError('email', { message: 'Could not verify email, try again' });
    } finally {
      setLoading(false);
    }
  }

  return (
    <form className='w-full max-w-[320px] space-y-8'>
      <button
        type='button'
        onClick={onBack}
        className='flex items-center gap-1 text-sm font-medium text-ws-text hover:underline'
      >
        <ChevronLeft className='w-4 h-4' />
        {t('common.back')}
      </button>

      <div className='text-center space-y-2'>
        <p className='text-sm text-ws-muted'>{t('auth.signup.step1.stepLabel')}</p>
        <h2 className='text-3xl font-semibold text-ws-text'>
          {t('auth.signup.step1.title')}
        </h2>
        <p className='text-base text-ws-muted'>
          {t('auth.signup.step1.subtitle')}
        </p>
      </div>

      <div className='space-y-6'>
        <FormField
          control={form.control}
          name='fullName'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-base font-medium'>{t('auth.signup.step1.fullName')}</FormLabel>
              <FormControl>
                <Input placeholder={t('auth.signup.step1.fullNamePlaceholder')} {...field} />
              </FormControl>
              <FormMessage />
              <p className='text-xs text-ws-muted'>
                {t('auth.signup.step1.fullNameHint')}
              </p>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='email'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-base font-medium'>{t('auth.signup.step1.email')}</FormLabel>
              <FormControl>
                <Input
                  type='email'
                  placeholder={t('auth.signup.step1.emailPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
              <p className='text-xs text-ws-muted'>
                {t('auth.signup.step1.emailHint')}
              </p>
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name='schoolName'
          render={({ field }) => (
            <FormItem>
              <FormLabel className='text-base font-medium'>{t('auth.signup.step1.school')}</FormLabel>
              <FormControl>
                <Input
                  placeholder={t('auth.signup.step1.schoolPlaceholder')}
                  {...field}
                />
              </FormControl>
              <FormMessage />
              <p className='text-xs text-ws-muted'>
                {t('auth.signup.step1.schoolHint')}
              </p>
            </FormItem>
          )}
        />
      </div>

      <Button variant='primary' size='lg' block onClick={handleNext} disabled={!canContinue} busy={loading}>
        {loading ? t('auth.signup.step1.checking') : t('auth.signup.step1.continue')}
      </Button>
    </form>
  );
}
